import React, { useState } from 'react'
import { Form } from 'react-bootstrap'
import { TopUpItem, TopUpSelection } from '../types'

interface Props {
  items: TopUpItem[]
  allowMultiple: boolean
  selectedTopUps: TopUpSelection[]
  selectedPlanCode: string
  onSelect: (itemCode: string, tierCode: string | null) => void
}

const formatCurrency = (amount: number): string => '₹' + amount.toLocaleString('en-IN')

const resolveTierPremium = (
  tier: { premiumAmount: number; planPremiums: { planCode: string; premiumAmount: number }[] | null },
  planCode: string
): number => {
  if (tier.planPremiums) {
    const match = tier.planPremiums.find((p) => p.planCode === planCode)
    if (match) return match.premiumAmount
  }
  return tier.premiumAmount
}

const TopUpSelector: React.FC<Props> = ({
  items,
  allowMultiple,
  selectedTopUps,
  selectedPlanCode,
  onSelect,
}) => {
  const [expandedTerms, setExpandedTerms] = useState<string[]>([])

  if (items.length === 0) return null

  const isItemSelected = (itemCode: string) =>
    selectedTopUps.some((s) => s.itemCode === itemCode)

  const getSelectedTier = (itemCode: string) =>
    selectedTopUps.find((s) => s.itemCode === itemCode)?.selectedTierCode ?? null

  const toggleTerms = (itemCode: string) => {
    setExpandedTerms((prev) =>
      prev.includes(itemCode) ? prev.filter((c) => c !== itemCode) : [...prev, itemCode]
    )
  }

  return (
    <div className='mb-6'>
      <label className='form-label fw-bolder fs-6'>Top-Up Coverage</label>

      {/* No top-up option (single mode) */}
      {!allowMultiple && (
        <div
          className={`border rounded p-3 mb-2 cursor-pointer ${
            selectedTopUps.length === 0
              ? 'border-primary bg-light-primary'
              : 'border-gray-300 bg-white'
          }`}
          onClick={() => {
            // Deselect all
            selectedTopUps.forEach((s) => onSelect(s.itemCode, null))
          }}
          role='button'
        >
          <div className='d-flex align-items-center'>
            <Form.Check
              type='radio'
              name='topup-select'
              checked={selectedTopUps.length === 0}
              onChange={() => selectedTopUps.forEach((s) => onSelect(s.itemCode, null))}
              className='me-3'
            />
            <div className='fw-bold'>No Top-Up</div>
          </div>
        </div>
      )}

      {items.map((item) => {
        const selected = isItemSelected(item.itemCode)
        const selectedTierCode = getSelectedTier(item.itemCode)
        const inputType = allowMultiple ? 'checkbox' : 'radio'

        // Default to first tier if selecting item
        const defaultTier = item.sumInsuredOptions[0]?.optionCode ?? null

        return (
          <div
            key={item.itemCode}
            className={`border rounded p-3 mb-2 ${
              selected ? 'border-primary bg-light-primary' : 'border-gray-300 bg-white'
            }`}
          >
            {/* Item header */}
            <div
              className='d-flex align-items-center cursor-pointer'
              onClick={() => {
                if (selected && allowMultiple) {
                  onSelect(item.itemCode, null)
                } else if (!selected) {
                  onSelect(item.itemCode, defaultTier)
                }
              }}
              role='button'
            >
              <Form.Check
                type={inputType}
                name={allowMultiple ? `topup-${item.itemCode}` : 'topup-select'}
                checked={selected}
                onChange={() => {
                  if (selected && allowMultiple) {
                    onSelect(item.itemCode, null)
                  } else {
                    onSelect(item.itemCode, defaultTier)
                  }
                }}
                className='me-3'
              />
              <div className='flex-grow-1'>
                <div className='fw-bolder fs-6'>{item.displayName}</div>
                {item.benefitOverrides?.description && (
                  <div className='text-muted fs-8 mt-1'>{item.benefitOverrides.description}</div>
                )}
              </div>
            </div>

            {/* SI tier options (if selected and multiple tiers) */}
            {selected && item.sumInsuredOptions.length > 1 && (
              <div className='mt-3 ms-8'>
                <div className='row g-2'>
                  {item.sumInsuredOptions.map((tier) => {
                    const tierSelected = selectedTierCode === tier.optionCode
                    const premium = resolveTierPremium(tier, selectedPlanCode)
                    return (
                      <div className='col-6 col-md-3' key={tier.optionCode}>
                        <div
                          className={`border rounded p-3 cursor-pointer text-center ${
                            tierSelected
                              ? 'border-primary bg-primary bg-opacity-10'
                              : 'border-gray-200'
                          }`}
                          onClick={() => onSelect(item.itemCode, tier.optionCode)}
                          role='button'
                        >
                          <div className='fw-bold fs-7'>{tier.displayLabel}</div>
                          <div className='text-muted fs-8'>{formatCurrency(premium)}</div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Single tier premium display */}
            {selected && item.sumInsuredOptions.length === 1 && (
              <div className='mt-2 ms-8 text-muted fs-7'>
                Premium: {formatCurrency(resolveTierPremium(item.sumInsuredOptions[0], selectedPlanCode))}
                {item.premiumModel === 'PerMember' && ' per member'}
              </div>
            )}

            {/* Terms */}
            {item.termsHtml && (
              <div className='mt-2 ms-8'>
                <button
                  className='btn btn-link btn-sm p-0 text-muted fs-8'
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleTerms(item.itemCode)
                  }}
                >
                  {expandedTerms.includes(item.itemCode) ? 'Hide Terms' : 'View Terms'}
                </button>
                {expandedTerms.includes(item.itemCode) && (
                  <div
                    className='mt-2 p-3 bg-light rounded fs-8'
                    dangerouslySetInnerHTML={{ __html: item.termsHtml }}
                  />
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default TopUpSelector
